import { changedPropertyConfig } from '@/config/changedPropertyConfig.js';
import { transformText } from '@/helpers/helpers.js';
import { formatValue } from '@/helpers/valueFormatter.js';

/**
 * Maps an entity-update `field` path to a human label + a value format type,
 * reusing the existing changedPropertyConfig + valueFormatter vocabulary.
 *
 * WebSocket entity updates carry two shapes of `field`:
 *  - short keys that already exist in changedPropertyConfig (`name`, `state`, `bid`…)
 *  - long Amazon Ads paths that don't
 *    (`optimizations.bidSettings.bidAdjustments.placementBidAdjustments[TOP_OF_SEARCH].percentage`)
 *
 * For the former we get the curated label/format for free; for the latter we
 * infer a format from the path tail and humanize the path so the raw dotted
 * string is never shown to the user.
 */

const ENTITY_LABELS = {
  campaign: 'Campaign',
  adgroup: 'Ad group',
  ad_group: 'Ad group',
  target: 'Target',
  ad: 'Ad',
  portfolio: 'Portfolio',
  negative_target: 'Negative target',
};

/**
 * Best-effort format type for an unmapped Amazon Ads field path.
 * Order matters: percentage is checked before currency because bid-adjustment
 * paths contain "bid" yet are percentages.
 * @param {string} field
 * @returns {string} a key understood by helpers/valueFormatter.formatValue
 */
function inferFormatType(field) {
  if (!field) return 'defaultText';
  const f = String(field).toLowerCase();
  if (f.includes('percent')) return 'percentInteger';
  if (
    f.includes('amount') ||
    f.includes('budget') ||
    f.includes('spend') ||
    f.includes('cost') ||
    f.includes('price')
  ) {
    return 'currency';
  }
  if (f.includes('date') || f.includes('time')) return 'dateTime';
  return 'defaultText';
}

/**
 * Turn a dotted/bracketed Amazon Ads field path into a short readable label.
 * Keeps the bracket token (e.g. `[TOP_OF_SEARCH]`) and the last segment only,
 * so the result stays compact.
 * @param {string} field
 * @returns {string}
 */
export function humanizeFieldPath(field) {
  if (!field) return '';
  const bracketMatch = String(field).match(/\[([^\]]+)\]/);
  const bracketLabel = bracketMatch ? transformText(bracketMatch[1]) : '';

  const segments = String(field)
    .replace(/\[[^\]]+\]/g, '')
    .split('.')
    .filter(Boolean);
  const lastLabel = transformText(segments[segments.length - 1] || field);

  return [bracketLabel, lastLabel].filter(Boolean).join(' · ') || String(field);
}

/**
 * Resolve label + format type for a changed field.
 * @param {string} field - `field` from an entity update's fields[] entry
 * @returns {{ label: string, formatType: string }}
 */
export function resolveChangedField(field) {
  const cfg = changedPropertyConfig[field];
  if (cfg) {
    return { label: cfg.shortName, formatType: cfg.formatType };
  }
  return { label: humanizeFieldPath(field), formatType: inferFormatType(field) };
}

/**
 * Whether two raw field values are effectively equal (i.e. not a real change).
 * Compares numerically when both parse as finite numbers (so "1" === "1.00"),
 * otherwise as trimmed strings.
 */
function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  return String(a).trim() === String(b).trim();
}

/**
 * Flatten a notification.data payload into renderable change rows, keeping only
 * fields that actually changed. The backend sometimes echoes touched-but-unchanged
 * fields (old_value === new_value) — those are dropped here.
 *
 * CREATED / DELETED rows are always kept (the field appeared / was removed).
 *
 * @param {Object} data - notification.data ({ entities: [...] })
 * @returns {Array<{ key, showEntity, entityLabel, entityId, label, changeType, oldText, newText }>}
 */
export function buildChangeRows(data) {
  const entities = Array.isArray(data?.entities) ? data.entities : [];
  const showEntity = entities.length > 1;
  const rows = [];

  entities.forEach((entity, ei) => {
    const entityLabel = ENTITY_LABELS[entity.entity_type] || transformText(entity.entity_type) || 'Entity';
    const entityId = entity.entity_id ?? entity.campaign_id ?? null;
    const fields = Array.isArray(entity.fields) ? entity.fields : [];

    fields.forEach((f, fi) => {
      const changeType = String(f.change_type || 'UPDATED').toUpperCase();

      // Drop no-op updates (touched field whose value didn't change).
      if (changeType === 'UPDATED' && valuesEqual(f.old_value, f.new_value)) return;

      const { label, formatType } = resolveChangedField(f.field);

      rows.push({
        key: `${entityId ?? ei}-${f.normalized_field || f.field || fi}`,
        showEntity,
        entityLabel,
        entityId,
        label,
        changeType,
        oldText: changeType === 'CREATED' ? null : formatValue(f.old_value, formatType),
        newText: changeType === 'DELETED' ? null : formatValue(f.new_value, formatType),
      });
    });
  });

  return rows;
}
